// src/pages/VerifyCode.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import StepBar from "../components/StepBar.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

const COOLDOWN = 30;

export default function VerifyCode() {
  const seo = seoPresets["/verify-code"];
  const { verifyEmailCode, verifyEmailToken, signupInit } = useAuth();
  const nav = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const emailQ = params.get("email") || "";
  const tokenQ = params.get("token") || "";
  const next = params.get("next") || "/set-password";

  const [email, setEmail] = useState(emailQ);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!emailQ && !tokenQ) nav("/signup", { replace: true });
  }, [emailQ, tokenQ, nav]);

  // If verify link with token was used (/verify-code?token=...)
  useEffect(() => {
    (async () => {
      if (!tokenQ) return;
      try {
        setLoading(true);
        await verifyEmailToken(tokenQ);
        nav(next, { replace: true });
      } catch (e) {
        setError(e?.message || "Verification failed.");
      } finally {
        setLoading(false);
      }
    })();
  }, [tokenQ, next, nav, verifyEmailToken]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const code = digits.join("");

  async function onSubmit(e) {
    e?.preventDefault();
    if (loading || code.length !== 6) return;
    setError("");
    setLoading(true);
    try {
      await verifyEmailCode({ email: (email || "").trim(), code });
      nav(next, { replace: true });
    } catch (e2) {
      setError(e2?.message || "Invalid code.");
      setDigits(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    if (!email || cooldown || loading) return;
    try {
      setLoading(true);
      setError("");
      await signupInit(email.trim());
      setCooldown(COOLDOWN);
    } catch (e2) {
      setError(e2?.message || "Couldn’t resend code.");
    } finally {
      setLoading(false);
    }
  }

  function onDigitChange(i, val) {
    const only = val.replace(/\D+/g, "").slice(0, 1);
    const nextDigits = [...digits];
    nextDigits[i] = only;
    setDigits(nextDigits);
    if (only && i < 5) inputsRef.current[i + 1]?.focus();
    if (nextDigits.join("").length === 6) onSubmit();
  }

  function onKeyDown(i, e) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const nextDigits = [...digits];
      nextDigits[i - 1] = "";
      setDigits(nextDigits);
      inputsRef.current[i - 1]?.focus();
    }
  }

  function onPaste(e) {
    const text = (e.clipboardData?.getData("text") || "")
      .replace(/\D+/g, "")
      .slice(0, 6);
    if (text.length) {
      e.preventDefault();
      const arr = text.split("").concat(Array(6).fill("")).slice(0, 6);
      setDigits(arr);
      if (arr.join("").length === 6) onSubmit();
    }
  }

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="auth-wrap">
        <style>{css}</style>

        <div className="auth-grid">
          {/* LEFT: verify flow */}
          <section className="auth-main">
            <header className="auth-header">
              <Link to="/" className="brand-link">
                <div className="logo">
                  <img
                    className="brand-icon"
                    src="https://imgur.com/ef4tlNy.png"
                    alt=""
                    aria-hidden
                  />
                </div>
                <div className="brand-names">
                  <span className="brand-top">MusicBase</span>
                  <span className="brand-sub">playlist outreach</span>
                </div>
              </Link>

              <div className="auth-switch">
                <button
                  type="button"
                  className="auth-switch-btn"
                  onClick={() => nav("/login")}
                >
                  Log in
                </button>
                <button type="button" className="auth-switch-btn active">
                  Sign up
                </button>
              </div>
            </header>

            <div className="auth-body">
              {/* STEP BAR ROW (TOP) */}
              <div className="auth-stephead">
                <div className="auth-stepbar">
                  <StepBar
                    steps={["Email", "Verify", "Password"]}
                    current={1}
                  />
                </div>

                <div className="auth-copy">
                  <h1 className="auth-title">Verify your email</h1>
                  <p className="auth-subtitle">
                    Enter the 6-digit code we sent to{" "}
                    <strong>{email || emailQ || "your email"}</strong>.
                  </p>
                </div>
              </div>

              {/* FORM */}
              <form onSubmit={onSubmit} className="form" noValidate>
                <div className={"field" + (email ? " has-value" : "")}>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder=" "
                  />
                  <label htmlFor="email">Email</label>
                </div>

                {/* 6 code boxes */}
                <div className="code-row" onPaste={onPaste}>
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputsRef.current[i] = el)}
                      className="code-box"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={d}
                      onChange={(e) => onDigitChange(i, e.target.value)}
                      onKeyDown={(e) => onKeyDown(i, e)}
                      aria-label={`Digit ${i + 1}`}
                    />
                  ))}
                </div>

                {error && (
                  <div className="error" role="alert">
                    {error}
                  </div>
                )}

                <button
                  className="submit"
                  type="submit"
                  disabled={loading || code.length !== 6 || !email}
                >
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </form>

              <div className="row-actions">
                <button
                  className="linklike"
                  onClick={onResend}
                  disabled={!email || cooldown || loading}
                >
                  {cooldown ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
                <span className="sep">•</span>
                <Link
                  to={`/signup?redirect=${encodeURIComponent(next)}`}
                  className="linklike"
                >
                  Use another email
                </Link>
              </div>
            </div>
          </section>

          {/* RIGHT: campaign preview (same as Signup) */}
          <aside className="auth-aside" aria-hidden="true">
            <div className="hero-shell">
              <div className="hero-pill">
                <span className="hero-dot" />
                <span>Campaign preview</span>
              </div>

              <h2 className="hero-title">
                See delivered and replies in one view.
              </h2>
              <p className="hero-text">
                As your campaigns run, MusicBase shows how many emails were
                delivered, bounced, or got a reply — the same metrics you see in
                your Campaigns table.
              </p>

              <div className="hero-card">
                <div className="hero-card-header">
                  <div className="hero-avatar">MB</div>
                  <div className="hero-card-text">
                    <div className="hero-card-title">Indie Pitch — Sample</div>
                    <div className="hero-card-sub">
                      Campaign overview · preview only
                    </div>
                  </div>
                </div>

                <div className="hero-rows">
                  <div className="hero-row">
                    <span className="hero-label">Delivered</span>
                    <span className="hero-value">54</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Replied</span>
                    <span className="hero-value">29</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Bounced</span>
                    <span className="hero-value">2</span>
                  </div>
                  <div className="hero-row">
                    <span className="hero-label">Spam</span>
                    <span className="hero-value">1</span>
                  </div>
                </div>
              </div>

              <div className="hero-metrics">
                <div>
                  <div className="hero-metric-number">Outreach stats</div>
                  <div className="hero-metric-label">
                    Delivered, bounced and replied counts for every campaign.
                  </div>
                </div>
                <div>
                  <div className="hero-metric-number">Your own inbox</div>
                  <div className="hero-metric-label">
                    Connect Gmail or Outlook so the data comes from your
                    mailbox, not guesses.
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

const css = `
:root{
  --bg:#f5f4ff;
  --surface:#ffffff;
  --surface-soft:#f9fafb;
  --muted:#6b7280;
  --text:#0b0b0c;
  --accent:#7c5cff;
  --accent-2:#00d4ff;
  --ring:rgba(124,92,255,.25);
  --border:#e5e7eb;
  --shadow:0 24px 70px rgba(15,23,42,.14);
}

/* keep auth page non-scrollable / centered (same as signup/login) */
html, body {
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

*{ box-sizing:border-box; }

.auth-wrap{
  min-height:100dvh;
  background:
    radial-gradient(1100px 650px at -10% -10%, rgba(124,92,255,.18), transparent 55%),
    radial-gradient(1100px 650px at 110% 0%, rgba(0,212,255,.18), transparent 55%),
    linear-gradient(#ffffff, #f9fafb);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px 16px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.auth-grid{
  max-width:1120px;
  background:var(--surface);
  border-radius:24px;
  box-shadow:var(--shadow);
  border:1px solid var(--border);
  display:grid;
  grid-template-columns:minmax(0,440px) minmax(0,1fr);
  overflow:hidden;
}

/* LEFT SIDE */
.auth-main{
  padding:28px 32px 26px;
  display:flex;
  flex-direction:column;
  background:linear-gradient(180deg,#ffffff,#f9fafb);
}

.auth-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:16px;
  margin-bottom:24px;
}

.brand-link{
  display:flex;
  align-items:center;
  gap:12px;
  text-decoration:none;
  color:inherit;
}
.logo{
  width:40px; height:40px;
  border-radius:14px;
  display:grid; place-items:center;
  font-size:18px; font-weight:900; color:#fff;
}
.brand-names{
  display:flex;
  flex-direction:column;
  line-height:1.05;
  align-items:flex-start;
  text-align:left;
}
.brand-top{font-weight:800; letter-spacing:.03em; font-size:15px;}
.brand-sub{font-size:11px; text-transform:uppercase; letter-spacing:.16em; color:var(--muted);}

/* toggle */
.auth-switch{
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  background:#f3f4ff;
  border:1px solid #e0e7ff;
}
.auth-switch-btn{
  border:none;
  background:transparent;
  color:#6b7280;
  padding:6px 14px;
  border-radius:999px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
}
.auth-switch-btn.active{
  background:#111827;
  color:#f9fafb;
}

/* body + stepbar */
.auth-body{
  flex:1;
  display:flex;
  flex-direction:column;
}

.auth-stephead{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:10px;
  margin-bottom:18px;
}
.auth-stepbar{
  width:100%;
  display:flex;
  justify-content:center;
}
.auth-stepbar > *{
  max-width:280px;
}
.auth-copy{
  max-width:280px;
  text-align:center;
}

/* titles */
.auth-title{
  margin:0 0 6px 0;
  font-size:30px;
  line-height:1.1;
  letter-spacing:-.02em;
  color:var(--text);
  text-align:center;
}
.auth-subtitle{
  margin:0 0 18px 0;
  font-size:14px;
  color:var(--muted);
  text-align:center;
}

/* form + fields */
.form{display:grid; gap:12px;}

.field{position:relative;}
.field input{
  width:100%;
  padding:16px 90px 10px 14px;
  border-radius:14px;
  border:1px solid var(--border);
  background:#ffffff;
  color:var(--text);
  font-size:14px;
  outline:none;
}
.field input::placeholder{color:transparent;}
.field label{
  position:absolute;
  left:14px;
  top:13px;
  pointer-events:none;
  color:var(--muted);
  background:#ffffff;
  padding:0 .25em;
  border-radius:999px;
  transition:all .16s ease;
  font-size:13px;
}
.field input:focus{
  box-shadow:0 0 0 5px var(--ring);
  border-color:#c4b5fd;
}
.field input:focus + label,
.field.has-value label{
  top:-8px;
  font-size:11px;
  color:#4b5563;
}

/* error + primary button */
.error{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#7f1d1d;
  padding:10px 12px;
  border-radius:12px;
  font-size:13px;
}

.submit{
  margin-top:4px;
  padding:13px 14px;
  border-radius:14px;
  font-weight:800;
  letter-spacing:.03em;
  border:none;
  cursor:pointer;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  color:#fff;
  box-shadow:0 10px 40px rgba(124,92,255,.35);
}
.submit:disabled{
  opacity:.7;
  cursor:default;
  box-shadow:none;
}

/* verification-code specific bits */
.code-row{
  display:flex;
  gap:10px;
  margin:8px 0 2px;
  justify-content:center;
}
.code-box{
  width:46px; height:52px;
  text-align:center;
  font-size:20px;
  font-weight:800;
  border-radius:12px;
  border:1px solid var(--border);
  outline:none;
  background:#fff;
  color:var(--text);
}
.code-box:focus{
  box-shadow:0 0 0 6px var(--ring);
  border-color:#cfd7ff;
}

/* row actions under form */
.row-actions{
  margin-top:10px;
  color:var(--muted);
  display:flex;
  gap:8px;
  align-items:center;
  justify-content:center;
}
.linklike{
  background:none;
  border:none;
  padding:0;
  color:var(--accent);
  font-weight:800;
  cursor:pointer;
}
.linklike[disabled]{
  color:#9aa0ae;
  cursor:not-allowed;
}
.sep{
  color:#c0c6d4;
}

/* RIGHT SIDE */
.auth-aside{
  padding:32px 40px;
  background:linear-gradient(135deg,#eef2ff,#fdfbff);
  display:flex;
  align-items:center;
  justify-content:center;
}
.hero-shell{
  max-width:380px;
}
.hero-pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  background:#111827;
  color:#e5e7eb;
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  margin-bottom:10px;
}
.hero-dot{
  width:7px; height:7px; border-radius:999px;
  background:linear-gradient(135deg,var(--accent),var(--accent-2));
}
.hero-title{
  margin:0 0 8px;
  font-size:24px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:#020617;
}
.hero-text{
  margin:0 0 18px;
  font-size:14px;
  color:#4b5563;
}

/* screenshot-style card */
.hero-card{
  background:#ffffff;
  border-radius:18px;
  padding:16px 16px 14px;
  border:1px solid rgba(148,163,184,.55);
  box-shadow:0 22px 60px rgba(15,23,42,.16);
  margin-bottom:18px;
}
.hero-card-header{
  display:flex;
  align-items:center;
  gap:10px;
  margin-bottom:10px;
}
.hero-avatar{
  width:34px; height:34px;
  border-radius:12px;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  display:grid; place-items:center;
  font-size:13px; font-weight:800; color:#fff;
}
.hero-card-text{
  display:flex;
  flex-direction:column;
}
.hero-card-title{
  font-size:14px;
  font-weight:600;
  color:#020617;
}
.hero-card-sub{
  font-size:12px;
  color:#6b7280;
}
.hero-rows{
  display:grid;
  gap:6px;
}
.hero-row{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:12px;
}
.hero-label{
  width:72px;
  color:#6b7280;
}
.hero-value{
  color:#111827;
}

/* metrics */
.hero-metrics{
  display:flex;
  flex-wrap:wrap;
  gap:18px;
}
.hero-metric-number{
  font-size:18px;
  font-weight:800;
  color:#020617;
}
.hero-metric-label{
  font-size:12px;
  color:#4b5563;
  max-width:220px;
}

/* responsive: match Signup.jsx */
@media (max-width: 960px){
  .auth-grid{
    border-radius:0px;
  }
  body {
    overflow-y: auto;
  }
  .auth-grid{
    grid-template-columns:1fr;
    max-width:560px;
  }
  .auth-aside{
    padding:24px 24px 12px;
  }
  .auth-main{
    padding:24px 22px 24px;
  }
  .auth-copy{
    max-width:none;
  }
}

@media (max-width: 560px){
  .auth-wrap{padding:0px;}
  .auth-header{align-items:flex-start;}
  .auth-switch{align-self:flex-end;}
}
`;
